import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import usePDPPhotos from 'data-hooks/usePDPPhotos/index';
import useModularPDPPhotos from 'data-hooks/useModularPDPPhotos';
import usePDPViewGalleryImageEvent from 'data-hooks/useEvents/usePDPViewGalleryImageEvent';
import usePDPCylindoViewer from 'data-hooks/usePDPCylindoViewer';

import usePdpSelectedCustomizerTab from 'components/consumer/PDP/state/pdpSelectedCustomizerTab/usePdpSelectedCustomizerTab';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import {
  getMainImageImgixPropsForPhoto,
  getThumbnailImageImgixPropsForPhoto,
} from './helpers/imgix-props';

import Prefetcher from '../../../essentials/Prefetcher';
import PDPDesktopHeroGalleryUI from './PDPDesktopHeroGalleryUI';
import usePDPDesktopHeroSizeProps from './helpers/usePDPDesktopHeroSizeProps';

const getPrefetcherConfig = (
  lastHoveredConfigPhotos,
  selectedPhoto,
  enableCrops,
  mainPhotoSizeProps
) => {
  if (!selectedPhoto || !lastHoveredConfigPhotos.length) {
    return {};
  }
  const mainPhoto =
    lastHoveredConfigPhotos[
      selectedPhoto.type === 'studio' ||
      !lastHoveredConfigPhotos[selectedPhoto.index]
        ? 0
        : selectedPhoto.index
    ];
  if (!mainPhoto.src) {
    return {};
  }
  const onRenderAssets = [
    ...lastHoveredConfigPhotos.slice(0, 5).map(({ src, type }) => ({
      url: src,
      imgixProps: getThumbnailImageImgixPropsForPhoto({ src, type }),
    })),
    {
      url: mainPhoto.src,
      imgixProps: {
        ...getMainImageImgixPropsForPhoto(mainPhoto, enableCrops),
        ...mainPhotoSizeProps,
      },
      isProductImage: true,
    },
  ];
  return { onRenderAssets };
};

const PDPDesktopHeroGallery = ({
  isEditOrder = false,
  mainPhotoLink,
  mode = 'pdp',
  pdpIsModularBYOProduct,
  slug,
  productHeroImage = null,
}) => {
  const { loading, error, data, actions } = usePDPPhotos(null, slug);
  const pdpSelectedCustomizerTab = usePdpSelectedCustomizerTab();

  let modularPhotosData = useModularPDPPhotos(null, slug);

  if (!pdpIsModularBYOProduct) {
    modularPhotosData = null;
  }

  const mainPhotoSizeProps = usePDPDesktopHeroSizeProps();
  const { viewGalleryImageEvent } = usePDPViewGalleryImageEvent();

  const {
    cylindoFeatures,
    isCylindoAvailable,
    cylindoConfigurationPhotos,
    isCylindoConfigurationPhotos,
    dimensionShotUrls,
    currentDimensionShot,
    selectedDimensionShotIndex,
    hasMultipleDimensionShots,
    shouldShowDimensionThumbnail,
    goToNextDimensionShot,
    goToPreviousDimensionShot,
  } = usePDPCylindoViewer({
    isPDPDesktop: true,
    deferCylindoFetch: mode === 'pdp',
  });

  const {
    campaign,
    configurationPhotos: nonCylindoConfigurationPhotos,
    dimensionalImage,
    enableCrops,
    introVideoIds,
    isConfigFlipped,
    isVideoSelected,
    lastHoveredConfigPhotos,
    productName,
    selectedPhoto,
    studioPhotos: allStudioPhotos,
  } = pdpIsModularBYOProduct ? modularPhotosData?.data ?? {} : data;

  const configurationPhotos = useRemoveBrokenImageUrls(
    isCylindoConfigurationPhotos
      ? cylindoConfigurationPhotos
      : nonCylindoConfigurationPhotos
  );

  const studioPhotos = useRemoveBrokenImageUrls(allStudioPhotos);

  const { setSelectedPhoto, setIsVideoSelected } = pdpIsModularBYOProduct
    ? modularPhotosData?.actions ?? {}
    : actions;

  const selectedPhotoToShow = useMemo(() => {
    if (selectedPhoto?.type === 'dimensional') {
      if (currentDimensionShot) {
        return {
          ...selectedPhoto,
          src: currentDimensionShot.src,
          dimensionCode: currentDimensionShot.dimensionCode,
        };
      }
      // Fallback to static dimensional image if no Cylindo data
      if (dimensionalImage?.src) {
        return {
          ...selectedPhoto,
          src: dimensionalImage.src,
        };
      }
      return selectedPhoto;
    }

    if (isCylindoConfigurationPhotos && selectedPhoto?.type === 'cylindo') {
      return selectedPhoto;
    }

    if (isCylindoAvailable && !selectedPhoto) {
      if (isCylindoConfigurationPhotos) {
        return cylindoConfigurationPhotos[0];
      }

      return {
        type: 'cylindo',
      };
    }

    const resolved = selectedPhoto || studioPhotos[0];

    if (
      resolved?.src ||
      resolved?.type === 'dimensional' ||
      resolved?.type === 'cylindo'
    ) {
      return resolved;
    }

    if (productHeroImage?.src) {
      return {
        type: 'studio',
        id: productHeroImage.src,
        src: productHeroImage.src,
      };
    }

    return resolved;
  }, [
    cylindoConfigurationPhotos,
    isCylindoAvailable,
    isCylindoConfigurationPhotos,
    selectedPhoto,
    studioPhotos,
    currentDimensionShot,
    dimensionalImage,
    productHeroImage,
  ]);

  // Create dimension shot object for both Cylindo API and static fallback images
  const currentDimensionShotToShow = useMemo(() => {
    if (currentDimensionShot) {
      return currentDimensionShot;
    }

    // Fallback: if showing dimensional type but no Cylindo data, create object from static image
    if (selectedPhoto?.type === 'dimensional' && dimensionalImage?.src) {
      return {
        src: dimensionalImage.src,
        dimensionCode: 'static',
        id: 'static-dimensional',
      };
    }

    return {
      src: '',
      dimensionCode: 'none',
      id: 'no-dimensional-shot',
    };
  }, [currentDimensionShot, selectedPhoto?.type, dimensionalImage]);

  if (loading && !productHeroImage?.src) {
    return (
      <PDPDesktopHeroGalleryUI
        loading
        mode={mode}
        showDecideLaterOverlay={pdpSelectedCustomizerTab === 'decide-later'}
      />
    );
  }

  if (!data) {
    return null;
  }

  if (error) {
    return 'Something went wrong, please try again';
  }

  return (
    <Prefetcher
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...getPrefetcherConfig(
        lastHoveredConfigPhotos,
        selectedPhoto,
        enableCrops,
        mainPhotoSizeProps
      )}
    >
      <PDPDesktopHeroGalleryUI
        configurationPhotos={configurationPhotos}
        selectedPhoto={selectedPhotoToShow}
        dimensionsImage={dimensionalImage}
        shouldShowDimensionThumbnail={shouldShowDimensionThumbnail}
        isEditOrder={isEditOrder}
        enableCrops={enableCrops}
        loading={false}
        showDecideLaterOverlay={pdpSelectedCustomizerTab === 'decide-later'}
        setSelectedPhoto={({ id, index, src, type }) => {
          setSelectedPhoto?.(
            type === 'cylindo'
              ? cylindoConfigurationPhotos[index]
              : { id, index, src, type }
          );
        }}
        dimensionShotUrls={dimensionShotUrls}
        currentDimensionShot={currentDimensionShotToShow}
        hasMultipleDimensionShots={hasMultipleDimensionShots}
        selectedDimensionShotIndex={selectedDimensionShotIndex}
        goToNextDimensionShot={goToNextDimensionShot}
        goToPreviousDimensionShot={goToPreviousDimensionShot}
        studioPhotos={studioPhotos}
        isConfigFlipped={isConfigFlipped}
        introVideoIds={introVideoIds}
        isVideoSelected={isVideoSelected}
        setIsVideoSelected={setIsVideoSelected}
        productName={productName}
        cylindoFeatures={cylindoFeatures}
        isCylindoAvailable={isCylindoAvailable}
        isCylindoConfigurationPhotos={isCylindoConfigurationPhotos}
        viewGalleryImageEvent={viewGalleryImageEvent}
        pdpIsModularBYOProduct={pdpIsModularBYOProduct}
        mode={mode}
        mainPhotoLink={mainPhotoLink}
        campaign={campaign}
        displayOnlyConfigImages={!isCylindoAvailable}
      />
    </Prefetcher>
  );
};

PDPDesktopHeroGallery.propTypes = {
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
  mainPhotoLink: PropTypes.string,
  mode: PropTypes.oneOf(['pdp', 'quickview']),
  slug: PropTypes.string,
  productHeroImage: PropTypes.shape({
    src: PropTypes.string.isRequired,
    width: PropTypes.number,
    height: PropTypes.number,
    alt: PropTypes.string,
  }),
};

export default PDPDesktopHeroGallery;
